import { useEffect } from "react";
import {
  useSendPresenceHeartbeatMutation,
  useLeavePresenceMutation,
} from "../../services/rtkApi/agentApi";
import { useAppSelector } from "../../store/hooks";

const HEARTBEAT_INTERVAL = 30_000;

/**
 * Registers the current user as present in a chat, but only while:
 *   - the user is logged in,
 *   - `active` is true (the chat view is actually open/visible), and
 *   - the browser tab is visible.
 *
 * When any of those stop being true we clear the interval and send `leave`,
 * so presence is never reported when the user isn't really looking at the chat.
 */
export function usePresenceHeartbeat(chatType: string, active: boolean) {
  const user = useAppSelector((s) => s.auth);
  const isLoggedIn = user.bootstrapStatus === "ready" && !!user.token;

  const [sendHeartbeat] = useSendPresenceHeartbeatMutation();
  const [leave] = useLeavePresenceMutation();

  useEffect(() => {
    if (!isLoggedIn || !active) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (intervalId) return;
      sendHeartbeat({ chatType });
      intervalId = setInterval(() => sendHeartbeat({ chatType }), HEARTBEAT_INTERVAL);
    };

    const stop = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        start();
      } else {
        stop();
        leave();
      }
    };

    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      stop();
      leave();
    };
  }, [isLoggedIn, active, chatType, sendHeartbeat, leave]);
}
